import Collapsible from "app/components/Collapsible/Collapsible";
import LoadingSpinner from "app/components/LoadingSpinner/LoadingSpinner";
import { ErrorType, FormDataType } from "app/types/fundingTypes";
import { useState } from "react";
import { FormattedMessage, injectIntl, IntlShape } from "react-intl";
import * as bankWireAPI from "../../../../../../../api/bankWireApi";
import {
  checkIbanOrAccountNumberSet,
  configureInitialFormData,
  getBeneficiaryBankDetails,
  getIntermediaryBankDetails,
  validateAccountNumberAndSortCode,
  validateIBANOrAccountNumber,
  validateRequiredFields,
} from "./withdrawalHelper";
import { WithdrawalTextInput } from "./WithdrawalTextInput/WithdrawalTextInput";

type Props = {
  I18n: {
    [key: string]: string;
  };
  setSuccess: Function;
  intl: IntlShape;
};

const Withdrawal = (props: Props) => {
  const { I18n, intl } = props;
  // Each field on the form should be added here
  // An input is then created for each field added
  const bankFields: Array<keyof FormDataType> = [
    "bankName",
    "bankAddress",
    "iban",
    "accountNumber",
    "sortCode",
    "swiftCode",
    "otherCode",
    "otherNotes",
    "amount",
  ];
  const intermediaryBankFields = [
    "intermediaryBankName",
    "intermediaryBankAddress",
    "intermediarySwiftCode",
  ];

  const requiredFields = ["bankName", "bankAddress", "swiftCode", "amount"];

  const initialFormData = configureInitialFormData(
    bankFields,
    intermediaryBankFields
  );
  const [formData, setFormData] = useState<FormDataType>(initialFormData);
  const [formErrors, setFormErrors] = useState(initialFormData);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [otherError, setOtherError] = useState("");
  const [
    showIntermediaryBankDetailsSection,
    toggleShowIntermediaryBankDetailsSection,
  ] = useState(false);

  const handleBlur = (id: string, value: string) => {
    if (requiredFields.includes(id) && value === "") {
      setError(id, intl.formatMessage({ id: "Required" }));
    } else {
      setError(id, "");
    }
    if (id === "iban" || id === "accountNumber" || id === "sortCode") {
      checkIbanOrAccountNumberSet(id, value, formData, setErrors, intl);
    }
  };

  const handleChange = (id: string, value: string) => {
    const cloneOfFormData = { ...formData };
    // Update the value of the field with the given ID
    cloneOfFormData[id] = value;
    setFormData(cloneOfFormData);
  };

  // Set one error
  const setError = (errorField: string, value: string) => {
    const cloneOfFormErrors = { ...formErrors };
    cloneOfFormErrors[errorField] = value;
    setFormErrors(cloneOfFormErrors);
  };

  // Set a list of errors
  const setErrors = (errors: ErrorType[]) => {
    const cloneOfFormErrors = { ...formErrors };
    errors.forEach(error => {
      const { errorField, value } = error;
      cloneOfFormErrors[errorField] = value;
    });
    setFormErrors(cloneOfFormErrors);
  };

  const validate = () => {
    const requiredFieldsErrors = validateRequiredFields(
      requiredFields,
      formData,
      intl
    );
    const ibanErrors = validateIBANOrAccountNumber(formData, intl);
    const accountNumberAndSortCodeError = validateAccountNumberAndSortCode(
      formData,
      intl
    );

    const errors = [
      ...requiredFieldsErrors,
      ...ibanErrors,
      ...accountNumberAndSortCodeError,
    ];

    setErrors(errors);
    const hasNonEmptyErrors = errors.some(error => error.value !== "");
    return !hasNonEmptyErrors;
  };

  const handleErrors = (errorData: any[]) => {
    // api returns errors in the following formats
    // error.field
    // EG {
    //     "field": "amount",
    //     "messages": [
    //         "Cashier Amount invalid, should not be null or empty."
    //     ]
    // },
    // error.details, show in a general error place
    // NOTE: am guessing this from previous UI code so take with a pinch of salt

    const formattedErrors: ErrorType[] = [];
    for (const err of errorData) {
      if (err.field) {
        // When setting the errors from api, need to differentiate between
        // intermediateBankDetails.bankAddress AND
        // beneficiaryBankDetails.bankAddress
        if (err.field.startsWith("intermediateBankDetails")) {
          const errorField = err.field.includes(".")
            ? err.field.split(".")[1]
            : err.field;
          let intError = { errorField: "", value: "" };
          switch (errorField) {
            case "swiftCode":
              intError = {
                errorField: "intermediarySwiftCode",
                value: err.messages[0] || "",
              };
              break;
            case "bankAddress":
              intError = {
                errorField: "intermediaryBankAddress",
                value: err.messages[0] || "",
              };
              break;
            case "bankName":
              intError = {
                errorField: "intermediaryBankName",
                value: err.messages[0] || "",
              };
              break;
            default:
              break;
          }
          formattedErrors.push(intError);
        } else {
          const errorField = err.field.includes(".")
            ? err.field.split(".")[1]
            : err.field;
          const errorMessage =
            err.messages && err.messages.length > 0 ? err.messages[0] : "";
          const formattedError = { errorField, value: errorMessage };
          formattedErrors.push(formattedError);
        }
      }
      if (err.details) {
        setOtherError("err.details");
      }
    }
    setErrors(formattedErrors);
  };

  const sendWithdrawRequest = (e: React.FormEvent) => {
    e.preventDefault();
    const isFormValid = validate();
    if (!isFormValid) {
      return;
    }
    const { amount } = formData;
    const beneficiaryBankData = getBeneficiaryBankDetails(formData);
    const intermediateBankData = getIntermediaryBankDetails(formData);
    setFormSubmitted(true);
    bankWireAPI
      .withdrawFunds(amount, beneficiaryBankData, intermediateBankData)
      .then(resp => {
        setFormSubmitted(false);
        props.setSuccess(true);
      })
      .catch(error => {
        const errorData = error.response.data.errors;
        handleErrors(errorData);
        setFormSubmitted(false);
      });
  };

  return (
    <div>
      <form onSubmit={sendWithdrawRequest}>
        <div className="form-inputs">
          <div className="form-inputs__header">
            <span className="form-inputs__header__text">
              <FormattedMessage id="cashier-name-bank-match" />
            </span>
          </div>
          <div className="form-inputs__column">
            <div className="form-inputs__column__header">
              <span className="form-inputs__column__header__text">
                <FormattedMessage id="cashier-your-bank-details" />
              </span>
            </div>
            {bankFields.map(field => {
              return (
                <WithdrawalTextInput
                  id={field}
                  label={I18n[field]}
                  handleChange={handleChange}
                  handleBlur={(id, value) => {
                    handleBlur(id, value);
                  }}
                  errorMessage={formErrors[field]}
                  key={field}
                  required={
                    requiredFields.includes(field) ||
                    field === "iban" ||
                    field === "accountNumber"
                  }
                  type={field === "amount" ? "number" : ""}
                />
              );
            })}
          </div>
          <div className="form-inputs__column">
            <Collapsible
              status={"open"}
              onToggle={() =>
                toggleShowIntermediaryBankDetailsSection(
                  !showIntermediaryBankDetailsSection
                )
              }
              header={
                <div className="collapsible-header">
                  <div className="form-inputs__column__header">
                    <span className="form-inputs__column__header__text">
                      <FormattedMessage id="cashier-intermediarybank-details" />
                    </span>
                  </div>
                </div>
              }
              content={intermediaryBankFields.map(field => {
                return (
                  <WithdrawalTextInput
                    id={field}
                    label={I18n[field]}
                    handleChange={handleChange}
                    handleBlur={(id, value) => {
                      handleBlur(id, value);
                    }}
                    errorMessage={formErrors[field]}
                    key={field}
                  />
                );
              })}
              isActive={showIntermediaryBankDetailsSection}
              withArrows
            />
          </div>
        </div>
        {formSubmitted ? (
          <LoadingSpinner />
        ) : (
          <>
            <button id="deposit_button" type="submit">
              <FormattedMessage tagName="span" id="cashier-withdraw-button" />
            </button>

            <div className="error-msg">{otherError}</div>
          </>
        )}
      </form>
    </div>
  );
};

export default injectIntl(Withdrawal);
